import type { GustWave, RippleSource } from "@bfly/engine";
import type { PortraitOutline } from "../portrait/face-outline.js";

/** A live wave's front, in viewport CSS pixels. `GustWave` gives its current center, mean
    radius, band thickness, edge growth, and draw; this adds the wave's id (the one in
    `WaveStart`) and its band's amplitude. The portrait sends every live wave's ring each
    frame, and the field, the text effects, the accents, and the bio measure from it
    (`gustOffset`, `gustReachesBox`). */
export interface WaveRing extends GustWave {
  id: number;
  amplitude: number;
  /** Where the wave comes from (the butterfly) right now, in viewport CSS pixels. */
  originX: number;
  originY: number;
  /** How far through its life the wave is, 0 to 1. */
  life: number;
}

/** A wave's cross-section at band offset `z`, in band thicknesses (positive is ahead of the
    front): a crest on the front, then a shallower trough behind it. It's 0 outside
    `[-trail, lead]`, so everything it moves settles back once the wave passes. The field's
    shaders (`WAVE_SHAPE_GLSL`) and the text effects use this same shape. `crest` is the
    crest's width as a fraction of the trough's. */
export const WAVE_SHAPE = Object.freeze({
  lead: 2,
  trail: 3,
  trough: 0.6,
  troughLag: 1.2,
  sharpness: 2,
  // The crest is half as wide as the trough. At full width the front looked too thick. A
  // wisp's wake keeps the full width (field-shaders.ts).
  crest: 0.5,
});

export function waveShape(z: number, crest: number = WAVE_SHAPE.crest): number {
  const { lead, trail, trough, troughLag, sharpness } = WAVE_SHAPE;
  if (z > lead || z < -trail) return 0;
  const front = z / crest;
  const behind = z + troughLag;
  return Math.exp(-sharpness * front * front) - trough * Math.exp(-sharpness * behind * behind);
}

/** `waveShape()` in GLSL, for the field's shaders. */
export const WAVE_SHAPE_GLSL = `
const float WAVE_LEAD = ${WAVE_SHAPE.lead.toFixed(1)};
const float WAVE_TRAIL = ${WAVE_SHAPE.trail.toFixed(1)};
const float WAVE_CREST = ${WAVE_SHAPE.crest.toFixed(2)};
float waveShape(float z, float crest) {
  if (z > WAVE_LEAD || z < -WAVE_TRAIL) return 0.0;
  float front = z / crest;
  float behind = z + ${WAVE_SHAPE.troughLag.toFixed(2)};
  float sharpness = ${WAVE_SHAPE.sharpness.toFixed(1)};
  return exp(-sharpness * front * front) - ${WAVE_SHAPE.trough.toFixed(2)} * exp(-sharpness * behind * behind);
}
`;

/** A wave that just started: its id (the same as its ring's), its effect level (1 to 5), and
    what started it. */
export interface WaveStart {
  id: number;
  level: number;
  source: RippleSource;
}

/** How the page hears about the face and the waves. */
export interface PortraitHooks {
  /** The face's outline: once the data loads, then after every layout change or scroll. */
  onOutline?: (outline: PortraitOutline) => void;
  /** Every live wave's ring, oldest first. Called each frame while any wave is live, then once
      with an empty list when the last one ends. Waves can overlap (`RippleSystem.canEmit`).

      The list and its rings get reused next frame (a ring can then hold a different wave), so
      read them during the call and copy anything you need to keep. */
  onWaves?: (rings: readonly WaveRing[]) => void;
  /** While this is true, no new wave starts: a click flutters the wings without one. */
  wavesHeld?: () => boolean;
}
